import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileText, Users, Plus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import SiteBreadcrumbs from './components/SiteBreadcrumbs';
import { useSite } from '@/hooks/sites/useSite';
import BulkInviteUsersModal from '@/pages/Submissions/components/BulkInviteUsersModal';
import UserManagement from '@/pages/Submissions/components/tabs/UserManagement';
import SiteActivity from '@/pages/Sites/components/SiteActivity';
import ScopedSubmissionsTable from '@/pages/Submissions/components/tabs/ScopedSubmissionsTable';
import SiteDocumentsContent from './components/SiteDocumentsContent';
import SiteFinalDocuments from '@/pages/Sites/components/SiteFinalDocuments';
import { getEntityStatusInfo } from '@/utils/entityStatuses';

const SiteShow: React.FC = () => {
  const navigate = useNavigate();
  const { siteId } = useParams<{ siteId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: site, isLoading, error } = useSite(siteId!);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [usersRefreshTrigger, setUsersRefreshTrigger] = useState(0);

  const handleAddSubmission = useCallback(() => {
    navigate(`/submissions/new?owner_id=${siteId}`);
  }, [navigate, siteId]);

  const handleInviteUsers = useCallback(() => {
    setIsInviteModalOpen(true);
  }, []);

  const handleCloseInviteModal = useCallback(() => {
    setIsInviteModalOpen(false);
  }, []);

  const handleInvitationSuccess = useCallback(() => {
    setTimeout(() => {
      setUsersRefreshTrigger(prev => prev + 1);
    }, 500);
  }, []);

  useEffect(() => {
    if (site) {
      const title = site.site_name || 'Site Details';
      document.title = `${title} - OneVerse`;
    }

    return () => {
      document.title = 'OneVerse';
    };
  }, [site, siteId]);

  const canManageUsers = site?.can_manage_users ?? false;

  const tabs = useMemo(
    () => [
      { id: 'submissions', label: 'Submissions' },
      { id: 'documents', label: 'Submitted Documents' },
      { id: 'final-documents', label: 'Final Documents' },
      ...(canManageUsers ? [{ id: 'users', label: 'Users' }] : []),
      { id: 'activity', label: 'Activity' },
    ],
    [canManageUsers]
  );

  const tabFromUrl = searchParams.get('tab');
  const validTabIds = tabs.map(tab => tab.id);
  const defaultTab = tabs[0]?.id ?? 'submissions';
  const activeTab =
    tabFromUrl && validTabIds.includes(tabFromUrl) ? tabFromUrl : defaultTab;

  const handleTabChange = useCallback(
    (tabId: string) => {
      setSearchParams({ tab: tabId });
    },
    [setSearchParams]
  );

  const renderTabContent = useCallback(() => {
    if (!site) return null;

    if (activeTab === 'submissions') {
      return (
        <ScopedSubmissionsTable
          ownerId={site.id}
          submissionScope="site"
          className="bg-white rounded-lg border border-gray-200 overflow-hidden"
        />
      );
    }

    if (activeTab === 'documents') {
      return <SiteDocumentsContent siteId={site.id} />;
    }

    if (activeTab === 'final-documents') {
      return <SiteFinalDocuments siteId={site.id} />;
    }

    if (activeTab === 'users' && canManageUsers) {
      return (
        <UserManagement
          resourcePath={`/sites/${site.id}`}
          canManageUsers={site.can_manage_users}
          refreshTrigger={usersRefreshTrigger}
          hierarchy="site"
          exportFilename={`site-${site.display_id ?? site.id}-users.csv`}
        />
      );
    }

    if (activeTab === 'activity') {
      return <SiteActivity siteId={site.id} />;
    }

    return null;
  }, [activeTab, site, canManageUsers, usersRefreshTrigger]);

  if (isLoading) {
    return (
      <div className="bg-white h-screen flex flex-col overflow-hidden">
        <div className="px-8 py-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-8 w-64" />
            </div>
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-8 w-36" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="mx-auto max-w-md">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {error instanceof Error ? error.message : 'Site not found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              The site you&apos;re looking for could not be loaded.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = site.status ? getEntityStatusInfo(site.status) : null;

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    });
  };

  const mappedAttributes = new Set(
    (site.mapped_fields ?? []).map(f => f.attribute)
  );

  const staticFields = [
    ...(!mappedAttributes.has('site_name')
      ? [{ label: 'Site Name', value: site.site_name }]
      : []),
    { label: 'Site ID', value: site.display_id },
    { label: 'Status', renderStatus: true },
    { label: 'Submitted', value: formatDate(site.submitted_at) },
    { label: 'Expires On', value: formatDate(site.expiration_date) },
    ...(!mappedAttributes.has('pi_name') && site.pi_name
      ? [{ label: 'Principal Investigator', value: site.pi_name }]
      : []),
    ...(!mappedAttributes.has('applicable_countries') &&
    site.countries?.length > 0
      ? [{ label: 'Country', value: site.countries[0] }]
      : []),
  ];

  const mappedFields = (site.mapped_fields ?? []).map(f => ({
    label: f.label,
    value: f.value,
  }));

  const detailFields = [...staticFields, ...mappedFields];

  return (
    <div className="bg-white h-screen flex flex-col overflow-hidden">
      {/* Page Header */}
      <div className="px-8 py-6 border-b border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-y-3">
          <div className="flex flex-col gap-1">
            <SiteBreadcrumbs site={site} />
            <h1 className="font-semibold text-2xl text-gray-900">
              {site.display_id || site.site_name || 'Site Details'}
            </h1>
          </div>

          {/* flex-wrap lets this action group drop to its own line on narrow
              viewports instead of squeezing or overflowing the header. */}
          <div className="flex flex-wrap items-center gap-3">
            {canManageUsers && (
              <button
                onClick={handleInviteUsers}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-sm"
              >
                <Users className="w-4 h-4" />
                <span>Invite Users</span>
              </button>
            )}

            {site.can_add_submissions && (
              <button
                onClick={handleAddSubmission}
                className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white text-sm font-medium px-3 py-1.5 rounded-md transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Add Submission</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body: scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Site Details card */}
        <div className="px-8 pt-8 pb-6">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-10">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Site Details
              </h3>
              <div className="grid grid-cols-4 gap-y-6 gap-x-12">
                {detailFields.map(field => (
                  <div key={field.label}>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      {field.label}
                    </p>
                    {field.renderStatus ? (
                      statusInfo ? (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: statusInfo.color }}
                          />
                          <span className="text-gray-900 text-sm">
                            {statusInfo.label}
                          </span>
                        </div>
                      ) : (
                        <p className="text-gray-900 text-sm">-</p>
                      )
                    ) : (
                      <p className="text-gray-900 text-sm leading-relaxed">
                        {field.value || '-'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        {tabs.length > 0 && (
          <div className="border-b border-gray-200 pt-6 px-8">
            <div className="flex gap-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 cursor-pointer ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tab content */}
        {tabs.length > 0 && (
          <div className="px-8 pt-8 pb-8">{renderTabContent()}</div>
        )}
      </div>

      <BulkInviteUsersModal
        isOpen={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        bulkInvitationsPath={`${site.show_path}/bulk_invitations`}
        assignableRolesPath={site.show_path}
        onSuccess={handleInvitationSuccess}
      />
    </div>
  );
};

export default SiteShow;
